import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useBooking } from "../context/BookingContext";

export interface Room {
  id: string;
  name: string;
  code: string;
  description: string | null;
  max_occupancy: number;
  base_rate: number;
  bookable: boolean;
  // Add other fields as needed but make them optional
  main_image_url?: string | null;
  images?: string[] | null;
  hotel_id?: string;
  // Add any other fields from your schema
}

export const useRealtimeRooms = () => {
  const { selectedHotel } = useBooking();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [hotelId, setHotelId] = useState<string | null>(null);

  // Use selectedHotel from context
  useEffect(() => {
    if (selectedHotel?.id) {
      console.log(
        "🏨 Using selected hotel:",
        selectedHotel.name,
        "(ID:",
        selectedHotel.id,
        ")"
      );
      setHotelId(selectedHotel.id);
    } else {
      console.warn("⚠️ No selected hotel, cannot fetch rooms");
      setHotelId(null);
      setLoading(false);
    }
  }, [selectedHotel?.id]); // Only depend on hotel ID

  // Then, fetch rooms once we have the hotel ID
  useEffect(() => {
    if (!hotelId) {
      setRooms([]);
      setLoading(false);
      return;
    }

    const fetchRooms = async () => {
      console.log("🔍 Fetching rooms for hotel ID:", hotelId);
      try {
        // Optimize query - only select needed fields
        const { data, error } = await supabase
          .from("room_types")
          .select(
            "id, name, code, description, max_occupancy, base_rate, bookable, display_order, images"
          )
          .eq("hotel_id", hotelId)
          .eq("bookable", true)
          .order("display_order", { ascending: true })
          .order("name", { ascending: true });

        if (error) {
          console.error("❌ Error fetching rooms:", error);
          setRooms([]);
          setLoading(false);
          return;
        }

        console.log("✅ Fetched rooms:", data?.length || 0);

        setRooms(data || []);
        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching rooms:", err);
        setRooms([]);
        setLoading(false);
      }
    };

    fetchRooms();

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn("⏰ Rooms fetch timeout - setting loading to false");
      setLoading(false);
    }, 10000); // 10 second timeout

    // Set up real-time subscription
    const channel = supabase
      .channel(`room_types_${hotelId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_types",
          filter: `hotel_id=eq.${hotelId}`,
        },
        (payload) => {
          console.log(
            "🔔 Real-time update received:",
            payload.eventType,
            payload
          );

          try {
            if (payload.eventType === "INSERT") {
              const newRoom = payload.new as Room;
              // Only add if bookable
              if (newRoom.bookable) {
                console.log("➕ Adding new room:", newRoom.name);
                setRooms((prev) => [...prev, newRoom]);
              }
            } else if (payload.eventType === "UPDATE") {
              const updatedRoom = payload.new as Room;
              console.log("🔄 Updating room:", updatedRoom.name);

              setRooms((prev) => {
                // Create a new array with updated room
                return prev.map((room) =>
                  room.id === updatedRoom.id ? updatedRoom : room
                );
              });
            } else if (payload.eventType === "DELETE") {
              const deletedId = payload.old.id;
              console.log("🗑️ Removing deleted room:", deletedId);
              setRooms((prev) => prev.filter((room) => room.id !== deletedId));
            }
          } catch (err) {
            console.error("❌ Error processing real-time update:", err);
          }
        }
      )
      .subscribe((status) => {
        console.log("📡 Room subscription status:", status);
      });

    return () => {
      console.log("Unsubscribing from room updates");
      clearTimeout(timeoutId);
      supabase.removeChannel(channel);
    };
  }, [hotelId]); // Only depend on hotelId

  return { rooms, loading };
};

// Other hooks in this file...
